import { extend } from 'vee-validate';
import {
  required,
  email,
  between,
  min,
  max,
  min_value,
  max_value,
  integer,
} from 'vee-validate/dist/rules';
import veeValidate from 'vee-validate/dist/locale/en.json';
import { isFunction } from 'lodash';
import { isValidURL } from '@/modules/core/helper';
import moment from 'moment';

// Add the required rule
extend('required', {
  ...required,
  message: (field) =>
    field ? i18n.$t(`Please specify {field}`, { field }) : i18n.$t('Please specify this field'),
});

/**
 * Add integer rule
 */
extend('integer', {
  ...integer,
  message: i18n.$t('Please enter an integer'),
});

/**
 * Add checkbox required
 */
extend('checkbox', {
  validate: (value) => value,
  message: (field) =>
    field ? i18n.$t(`Please specify {field}`, { field }) : i18n.$t('Please specify this field'),
});

extend('email', email);

/**
 * Validates any url with the regEx specified in 'isValidURL' method
 */
extend('url', (url) => {
  if (!isValidURL(url)) {
    return i18n.$t('Please enter a valid url');
  }
  return true;
});

/**
 * Rule should be in the given format ---> "'between:min,max'"
 * Validates whether a given value is in between mix and max (includes min and max)
 */
extend('between', {
  params: ['min', 'max'],
  ...between,
  message: (field, params) => i18n.$t('Value should be between {min} and {max}', params),
});

/**
 * To pass any custom validation function.
 * Check seo/website/websiteForm/competitorStep for an example or check
 * https://github.com/logaretm/vee-validate/issues/2736#issuecomment-617322253
 */
extend('custom', {
  params: ['validators'],
  /**
   *
   * @param value
   * @param validators {CustomValidator []}
   * @return {boolean}
   */
  validate: (value, { validators }) => !validators.find((validator) => !validator.validate(value)),
  /**
   *
   * @param fieldName
   * @param _value_
   * @param validators {CustomValidator []}
   * @return {string|null}
   */
  message: (fieldName, { _value_, validators }) => {
    const failedValidator = validators.find((validator) => !validator.validate(_value_));

    let outputMessage = null;
    if (failedValidator) {
      outputMessage = isFunction(failedValidator.message)
        ? failedValidator.message(_value_)
        : failedValidator.message;
    }
    return outputMessage;
  },
});

// Adding custom message for min
extend('min', {
  ...min,
  message: veeValidate.messages.min,
});

// Adding custom message for max
extend('max', {
  ...max,
  message: veeValidate.messages.max,
});

// Adding custom message for min_value
extend('min_value', {
  ...min_value,
  message: veeValidate.messages.min_value,
});

// Adding custom message for max_value
extend('max_value', {
  ...max_value,
  message: veeValidate.messages.max_value,
});

/**
 * Rule should be in given format -- "'array_min:arrayLength'"
 * To validate array length should not be less than specified length, we can use it for multiselect and checkbox group
 */
extend('array_min', {
  params: ['arrayLength'],
  validate: (value, { arrayLength }) => value.length >= arrayLength,
  message: (fieldName, args) =>
    `Should have minimum ${args.arrayLength} ${
      args.arrayLength > 1 ? 'options' : 'option'
    } selected`,
});

/**
 * Rule should be in given format -- "'array_max:arrayLength'"
 * To validate array length should not be greater than specified length, we can use it for multiselect and checkbox group
 */
extend('array_max', {
  params: ['arrayLength'],
  validate: (value, { arrayLength }) => value.length <= arrayLength,
  message: (fieldName, args) =>
    `Should have maximum ${args.arrayLength} ${
      args.arrayLength > 1 ? 'options' : 'option'
    } selected`,
});

/**
 * Rule should be in given format -- "'array_between:minLength,maxLength'"
 * To validate array length should be between specified min and max, we can use it for string, multiselect and checkbox group
 */
extend('array_between', {
  params: ['minLength', 'maxLength'],
  validate: (value, { minLength, maxLength }) =>
    minLength <= value.length && value.length <= maxLength,
  message: (fieldName, args) =>
    `Selected options should be between ${args.minLength} and ${args.maxLength}`,
});

/**
 * Rule should be in given format -- "'starts_with:ruleValue'"
 * To validate input field value should start with specified string.
 */
extend('starts_with', {
  validate: (value, { ruleValue, isCaseSensitive }) => {
    if (!isCaseSensitive) {
      return value?.toLowerCase().startsWith(ruleValue?.toLowerCase());
    }
    return value.startsWith(ruleValue);
  },
  params: ['ruleValue', 'isCaseSensitive'],
  message: 'The value entered should starts with {ruleValue}',
});

/**
 *  Rule should be in given format -- "'ends_with:ruleValue'"
 * To validate input field value should ends with specified string.
 */
extend('ends_with', {
  validate: (value, { ruleValue, isCaseSensitive }) => {
    if (!isCaseSensitive) {
      return value?.toLowerCase().endsWith(ruleValue?.toLowerCase());
    }
    return value.endsWith(ruleValue);
  },
  params: ['ruleValue'],
  message: 'The value entered should ends with {ruleValue}',
});

/**
 * Rule should be in given format -- "'contains:ruleValue'"
 * To validate input field value should contains specified string.
 */
extend('contains', {
  validate: (value, { ruleValue, isCaseSensitive }) => {
    if (!isCaseSensitive) {
      return value?.toLowerCase().includes(ruleValue?.toLowerCase());
    }
    return value.includes(ruleValue);
  },
  params: ['ruleValue'],
  message: 'The {_field_} should contain {ruleValue}',
});

/**
 * Rule should be in given format -- "'not_contains:ruleValue'"
 * Vee Validate Custom rule to validate input field value should not contain particular string.
 */
extend('not_contains', {
  validate: (value, { ruleValue, isCaseSensitive }) => {
    if (!isCaseSensitive) {
      return value?.toLowerCase().includes(ruleValue?.toLowerCase()) === -1;
    }
    return value.indexOf(ruleValue) === -1;
  },
  params: ['ruleValue'],
  message: 'The {_field_} should not contain {ruleValue}',
});

extend('is_equal', {
  validate: (value, { ruleValue }) => value === ruleValue,
  params: ['ruleValue'],
  message: 'The {_field_} should be equal to {ruleValue}',
});

extend('is_not_equal', {
  validate: (value, { ruleValue }) => value !== ruleValue,
  params: ['ruleValue'],
  message: 'The {_field_} should not be equal to {ruleValue}',
});

/**
 *  To validate value length should be equal to specified length.
 */
extend('array_is_equal', {
  validate: (value, { ruleValue }) => value.length === Number(ruleValue),
  params: ['ruleValue'],
  message: 'The {_field_} field must be {ruleValue} long',
});

/**
 *  To validate value length should not be equal to specified length.
 */
extend('array_is_not_equal', {
  validate: (value, { ruleValue }) => value.length !== Number(ruleValue),
  params: ['ruleValue'],
  message: 'The {_field_} field should not be {ruleValue} long',
});

// TODO: @Ramya will update dateformat in next PR, still decisions are pending about date format.
/**
 * To validate value should be before ruleValue.
 */
extend('before_date', {
  validate: (value, { ruleValue }) => {
    if (moment(ruleValue).isValid()) {
      return value < new Date(ruleValue);
    }
    return true;
  },
  params: ['ruleValue'],
  message: (fieldName, args) => {
    const limit = moment(args.ruleValue).isValid()
      ? moment(args.ruleValue).format('DD/MM/YYYY')
      : args.ruleValue;

    return `${fieldName} should be before ${limit}`;
  },
});

/**
 * To validate value should be before ruleValue.
 */
extend('before_time', {
  validate: (value, { ruleValue }) => {
    const expectedValue = moment({
      h: new Date(ruleValue).getHours(),
      s: new Date(ruleValue).getMinutes(),
    });
    const fieldValue = moment({ h: new Date(value).getHours(), s: new Date(value).getMinutes() });
    return fieldValue.isBefore(expectedValue);
  },
  params: ['ruleValue'],
  message: (fieldName, args) =>
    `${fieldName} should be before ${moment(args.ruleValue).format('hh:mm:ss a')}`,
});

/**
 * To validate value should be on or before ruleValue.
 */
extend('on_or_before_date', {
  validate: (value, { ruleValue }) => value <= new Date(ruleValue),
  params: ['ruleValue'],
  message: (fieldName, args) => {
    const limit = moment(args.ruleValue).isValid()
      ? moment(args.ruleValue).format('DD/MM/YYYY')
      : args.ruleValue;

    return `${fieldName} should be on or before ${limit}`;
  },
});

/**
 * To validate value should be at or before ruleValue.
 */
extend('at_or_before_time', {
  validate: (value, { ruleValue }) => {
    const expectedValue = moment({
      h: new Date(ruleValue).getHours(),
      s: new Date(ruleValue).getMinutes(),
    });
    const fieldValue = moment({ h: new Date(value).getHours(), s: new Date(value).getMinutes() });
    return fieldValue.isSameOrBefore(expectedValue) || fieldValue === expectedValue;
  },
  params: ['ruleValue'],
  message: (fieldName, args) =>
    `${fieldName} should be at or before ${moment(args.ruleValue).format('hh:mm:ss a')}`,
});

/**
 * Vee Validate Custom rule to validate value should be on or after ruleValue.
 */
extend('on_or_after_date', {
  validate: (value, { ruleValue }) => value >= new Date(ruleValue),
  params: ['ruleValue'],
  message: (fieldName, args) => {
    const limit = moment(args.ruleValue).isValid()
      ? moment(args.ruleValue).format('DD/MM/YYYY')
      : args.ruleValue;

    return `${fieldName} should be on or after ${limit}`;
  },
});

/**
 * Vee Validate Custom rule to validate value should be at or after ruleValue.
 */
extend('at_or_after_time', {
  validate: (value, { ruleValue }) => {
    const expectedValue = moment({
      h: new Date(ruleValue).getHours(),
      s: new Date(ruleValue).getMinutes(),
    });
    const fieldValue = moment({ h: new Date(value).getHours(), s: new Date(value).getMinutes() });
    return fieldValue.isSameOrAfter(expectedValue);
  },
  params: ['ruleValue'],
  message: (fieldName, args) =>
    `${fieldName} should be at or after ${moment(args.ruleValue).format('hh:mm:ss a')}`,
});

/**
 * Vee Validate Custom rule to validate value should be after ruleValue.
 */
extend('after_date', {
  validate: (value, { ruleValue }) => {
    if (moment(ruleValue).isValid()) {
      return value > new Date(ruleValue);
    }
    return true;
  },
  params: ['ruleValue'],
  message: (fieldName, args) => {
    const limit = moment(args.ruleValue).isValid()
      ? moment(args.ruleValue).format('DD/MM/YYYY')
      : args.ruleValue;

    return `${fieldName} should be after ${limit}`;
  },
});

/**
 * Vee Validate Custom rule to validate value should be after ruleValue.
 */
extend('after_time', {
  validate: (value, { ruleValue }) => {
    const expectedValue = moment({
      h: new Date(ruleValue).getHours(),
      s: new Date(ruleValue).getMinutes(),
    });
    const fieldValue = moment({ h: new Date(value).getHours(), s: new Date(value).getMinutes() });
    return fieldValue.isAfter(expectedValue);
  },
  params: ['ruleValue'],
  message: (fieldName, args) =>
    `${fieldName} should be after ${moment(args.ruleValue).format('hh:mm:ss a')}`,
});

/**
 * To validate input field value is a list of email addresses separated by comma.
 */
extend('comma_separated_emails', {
  validate(value) {
    const emailArray = value.split(',').map((singleEmail) => singleEmail.trim());
    return emailArray.every((singleEmail) =>
      /^[\w+\-.%]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(singleEmail)
    );
  },
  message: i18n.$t('The email addresses should be separated by comma'),
});

/**
 * Vee Validate Custom rule to validate value should be equal to string ruleValue.
 */
extend('is_equal_to_string', {
  validate: (value, { ruleValue, isCaseSensitive }) => {
    if (!isCaseSensitive) {
      return value?.toLowerCase() === ruleValue?.toLowerCase();
    }
    return value === ruleValue;
  },
  params: ['ruleValue'],
  message: 'The {_field_} should be equal to {ruleValue}',
});

extend('is_not_equal_to_string', {
  validate: (value, { ruleValue, isCaseSensitive }) => {
    if (!isCaseSensitive) {
      return value?.toLowerCase() !== ruleValue?.toLowerCase();
    }
    return value !== ruleValue;
  },
  params: ['ruleValue'],
  message: 'The {_field_} should not be equal to {ruleValue}',
});
